import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Play } from 'lucide-react'
import { AvailableCourses } from './ui/AvailableCourses'
import { coursesAPI } from '../api/courses'
import { exercisesAPI } from '../api/exercises'
import { dashboardAPI } from '../api/dashboard'
import { appInfo } from '@renderer/config/appinfo'
import iconSrc from '../../../../resources/icon.png'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'
import type { Course } from '../types/api'

type CurrentActivity = {
  exercise: { id: number; title: string }
  lab_module: { id: number; course_id: number }
  total_modules: number
  current_module: number
  thumbnail: string | null
  next_module: { id: number; title: string } | null
}

export const MainPage = (): React.JSX.Element => {
  const navigate = useNavigate()
  const { setBreadcrumbs } = useBreadcrumbs()

  const [courses, setCourses] = useState<Course[]>([])
  const [activity, setActivity] = useState<CurrentActivity | null>(null)
  const [heroProgress, setHeroProgress] = useState(0)
  const [completedModules, setCompletedModules] = useState(0)
  const [totalModules, setTotalModules] = useState(0)

  useEffect(() => {
    setBreadcrumbs([{ label: 'Courses' }])
  }, [])

  useEffect(() => {
    coursesAPI.getAllCourses().then(setCourses).catch(() => {})
    dashboardAPI
      .getLabProgressSummary()
      .then((data) => {
        setCompletedModules(data.completed_modules)
        setTotalModules(data.total_modules)
      })
      .catch(() => {})
    exercisesAPI
      .getCurrentExercise()
      .then((data) => setActivity(data))
      .catch(() => setActivity(null))
  }, [])

  const heroCourseId = activity?.lab_module.course_id ?? courses[0]?.id ?? null
  const heroCourse = courses.find((c) => c.id === heroCourseId) ?? courses[0] ?? null

  useEffect(() => {
    if (heroCourseId == null) return
    coursesAPI
      .getCourseModules(String(heroCourseId))
      .then((modules: Array<{ progress?: number }>) => {
        const pct = modules.length
          ? Math.round(modules.reduce((sum, m) => sum + (m.progress ?? 0), 0) / modules.length)
          : 0
        setHeroProgress(pct)
      })
      .catch(() => setHeroProgress(0))
  }, [heroCourseId])

  const heroImage = activity?.thumbnail
    ? `${appInfo.apiDomain}/storage/${activity.thumbnail}`
    : heroCourse?.thumbnail
      ? `${appInfo.apiDomain}/storage/${heroCourse.thumbnail}`
      : iconSrc

  const heroButtonLabel = !activity ? 'Explore Course' : heroProgress === 0 ? 'Start Learning' : 'Resume Learning'

  const handleHeroAction = (): void => {
    if (activity) {
      navigate(`/exercise/${activity.exercise.id}/chat`)
      return
    }
    if (heroCourse) navigate(`/course/${heroCourse.id}/modules`)
  }

  const memberSince = courses[0]
    ? new Date(courses[0].created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : '—'

  return (
    <div className="max-w-[1200px] mx-auto space-y-5">
      {heroCourse && (
        <div className="relative w-full overflow-hidden rounded-2xl shadow-lg">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${heroImage}')` }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(36,21,0,0.8) 0%, rgba(0,0,0,0.8) 100%)' }}
          />
          <div
            className="relative z-10 flex flex-col p-4 sm:p-5"
            style={{ minHeight: 'clamp(240px, 24vw, 320px)' }}
          >
            <span className="w-fit inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white mb-2.5">
              {activity ? 'IN PROGRESS' : 'COURSE'}
            </span>

            <h2
              className="font-bold text-white mb-1.5"
              style={{ fontSize: 'clamp(1.05rem, 2vw, 1.5rem)' }}
            >
              {heroCourse.title}
            </h2>

            {heroCourse.description && (
              <p className="max-w-xl mb-2.5 text-xs leading-relaxed text-white/70 line-clamp-2">
                {heroCourse.description}
              </p>
            )}

            <div className="flex-1" />

            <div className="flex flex-col gap-2.5">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[11px] font-medium text-white/60">Course Progress</span>
                  <span className="text-xs font-bold text-[#D4AF37]">{heroProgress}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-white/20">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{ width: `${heroProgress}%`, background: 'linear-gradient(to right, #B8860B, #D4AF37)' }}
                  />
                </div>
              </div>
              <button
                onClick={handleHeroAction}
                className="flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ background: '#7a6020' }}
              >
                <Play className="w-3 h-3 fill-white" />
                {heroButtonLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="grid grid-cols-3 gap-px overflow-hidden rounded-xl"
        style={{ background: '#e0d4be', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <div className="bg-white px-3 py-3 text-center">
          <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-[#7a6020]/80">My Courses</p>
          <p className="mt-0.5 truncate text-sm font-bold text-[#181820]">{courses.length}</p>
          <p className="truncate text-[9px] text-[#5A5A6D]">Enrolled</p>
        </div>
        <div className="bg-white px-3 py-3 text-center">
          <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-[#7a6020]/80">Progress</p>
          <p className="mt-0.5 truncate text-sm font-bold text-[#181820]">
            {completedModules} / {totalModules}
          </p>
          <p className="truncate text-[9px] text-[#5A5A6D]">Modules completed</p>
        </div>
        <div className="bg-white px-3 py-3 text-center">
          <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-[#7a6020]/80">Member Since</p>
          <p className="mt-0.5 truncate text-sm font-bold text-[#181820]">{memberSince}</p>
          <p className="truncate text-[9px] text-[#5A5A6D]">Across all courses</p>
        </div>
      </div>

      <AvailableCourses />
    </div>
  )
}
